import { cn } from "@/lib/utils";
import { Crown } from "lucide-react";
import { RocketCoin } from "@/components/icons/RocketCoin";
import { Shield } from "lucide-react";
import { Cog, Wrench } from "lucide-react";
import { DripIcon } from "@/components/icons/Drip";

interface LogoProps {
  className?: string;
  size?: "small" | "default";
  showCrown?: boolean;
  showRocket?: boolean;
  showShield?: boolean;
  showTools?: boolean;
  showDrip?: boolean;
  rocketMirrored?: boolean;
  spin?: "clockwise" | "counterclockwise" | false;
  customText?: string;
}

export function Logo({ className, size = "default", showCrown = false, showRocket = false, showShield = false, showTools = false, showDrip = false, rocketMirrored = false, spin = false, customText }: LogoProps) {
  const sizeClass = size === "small" ? "w-8 h-8" : "w-12 h-12";
  const spinClass = spin === "clockwise" ? "[&>svg]:animate-spin-clockwise" : 
                   spin === "counterclockwise" ? "[&>svg]:animate-spin-counterclockwise" : "";
  
  return (
    <div className={cn("relative aspect-square", sizeClass, className)}>
      {/* Spinning outer layer */}
      <div className={cn("absolute inset-0 outer-square")}>
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 32 32"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="[&_rect]:origin-center"
        >
          {/* Outer square with text color border */}
          <rect x="0" y="0" width="32" height="32" rx="4" fill="transparent"/>
          <rect x="1" y="1" width="30" height="30" rx="3" stroke="var(--color-text, #ffffff)" strokeWidth="2"/>
        </svg>
      </div>
      
      {/* Static inner circle and text/icon */}
      <div className="absolute inset-0">
        <svg
          width="100%"
          height="100%"
          viewBox="0 0 32 32"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Inner circle with primary color border */}
          <circle cx="16" cy="16" r="12" fill="transparent" stroke="var(--color-primary, #0A74FF)" strokeWidth="2"/>
          {showCrown ? (
            <foreignObject x="8" y="8" width="16" height="16">
              <Crown className="w-full h-full text-[#EAB308]" />
            </foreignObject>
          ) : showRocket ? (
            <foreignObject x="7" y="7" width="18" height="18">
              <RocketCoin className="w-full h-full" mirrored={rocketMirrored} />
            </foreignObject>
          ) : showShield ? (
            <foreignObject x="7" y="7" width="18" height="18">
              <Shield className="w-full h-full text-[var(--color-header-accent,#0A74FF)]" />
            </foreignObject>
          ) : showTools ? (
            <foreignObject x="7" y="7" width="18" height="18">
              <div className="relative w-full h-full flex items-center justify-center">
                <Cog className="w-full h-full absolute animate-spin-slow text-[var(--color-header-accent,#0A74FF)]" />
                <Wrench className="w-3/4 h-3/4 absolute text-[var(--color-header-accent,#0A74FF)]" />
              </div>
            </foreignObject>
          ) : showDrip ? (
            <foreignObject x="7" y="7" width="18" height="18">
              <div className="relative w-full h-full flex items-center justify-center">
                <DripIcon className="w-full h-full" style={{ color: 'var(--color-header-accent, #0A74FF)' }} />
              </div>
            </foreignObject>
          ) : (
            <text 
              x="16" 
              y="16" 
              fontSize={customText ? "6.5" : "7.5"}
              fontFamily="Arial Black, Arial, sans-serif" 
              fontWeight="900" 
              fill="var(--color-text, #ffffff)" 
              textAnchor="middle"
              dominantBaseline="central"
              letterSpacing="-0.3"
            >
              {customText || "MDB"}
            </text>
          )}
        </svg>
      </div>
    </div>
  );
}